import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CreditCard, Check, CircleNotch, ArrowSquareOut, Sparkle, Warning } from '@phosphor-icons/react';

const PLAN_FEATURES = {
  starter: ['AI guest chat (web + shared SMS)', 'Personalised Welcome Guide PDF', 'Local area guide from your address', 'Daily dashboard briefing', 'Email support'],
  pro: ['Everything in Starter', 'AI voice agent (ElevenLabs)', 'AI maintenance triage + auto-dispatch', 'Photo-verified cleaning', 'Review Request SMS', 'Airbnb/Vrbo/Booking.com iCal sync', 'Priority support'],
  scale: ['Everything in Pro', 'Dedicated SMS & phone per property', 'Proactive pre-arrival AI call', 'Property inspections tracking', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee'],
};

export default function BillingPage() {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [annual, setAnnual] = useState(true);
  const [qty, setQty] = useState(1);
  const [startingCheckout, setStartingCheckout] = useState(null); // plan_key while creating
  const [portalLoading, setPortalLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get('/billing/plans'), api.get('/billing/subscription')]);
      setPlans(p.data.plans);
      setSub(s.data);
      if (s.data.subscription_quantity) setQty(s.data.subscription_quantity);
      if (s.data.subscription_interval === 'month') setAnnual(false);
    } catch {
      toast.error('Failed to load billing details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    if (canceled) {
      toast.info('Checkout canceled.');
      searchParams.delete('canceled');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (success && sessionId) {
      (async () => {
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
            if (data.payment_status === 'paid') {
              toast.success(`Subscription active — ${data.plan_key} (${data.interval})`);
              searchParams.delete('success'); searchParams.delete('session_id');
              setSearchParams(searchParams, { replace: true });
              load();
              return;
            }
          } catch { /* ignore */ }
          if (attempts < 6) setTimeout(poll, 3000);
          else {
            toast('Payment is still processing. Refresh in a minute.', { icon: '⏳' });
            searchParams.delete('success'); searchParams.delete('session_id');
            setSearchParams(searchParams, { replace: true });
          }
        };
        poll();
      })();
    }
    // eslint-disable-next-line
  }, []);

  const startCheckout = async (plan_key) => {
    setStartingCheckout(plan_key);
    try {
      const { data } = await api.post('/billing/checkout', {
        plan_key, interval: annual ? 'year' : 'month', quantity: qty,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start checkout');
      setStartingCheckout(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/billing/portal', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500" data-testid="billing-loading">Loading…</div>;

  const hasActiveSub = sub?.has_subscription && ['active', 'trialing', 'past_due'].includes(sub?.status);
  const cpe = sub?.current_period_end ? new Date(sub.current_period_end) : null;

  return (
    <div className="max-w-5xl space-y-8" data-testid="billing-page">
      <div>
        <h1 className="text-2xl font-black tracking-tight text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Billing</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage your HostCockpit subscription. Secure checkout powered by Stripe.</p>
      </div>

      {/* Current subscription banner */}
      {hasActiveSub ? (
        <div className="border border-[#0052FF]/30 bg-gradient-to-br from-blue-50 to-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center justify-between" data-testid="current-subscription-card">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 bg-[#0052FF] rounded-sm flex items-center justify-center flex-shrink-0"><Sparkle size={22} weight="fill" className="text-white" /></div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0052FF] mb-0.5">Current plan</div>
              <div className="text-lg font-black text-zinc-950 capitalize" style={{ fontFamily: 'var(--font-heading)' }}>
                {sub.plan_key} · {sub.quantity} {sub.quantity === 1 ? 'property' : 'properties'} · {sub.interval === 'year' ? 'Annual' : 'Monthly'}
              </div>
              <div className="text-xs text-zinc-600 mt-1">
                Status: <span className="font-bold capitalize">{sub.status}</span>
                {sub.cancel_at_period_end && <span className="ml-2 text-amber-700 font-bold">· Cancels at period end</span>}
                {cpe && <span className="ml-2">· Renews {cpe.toLocaleDateString()}</span>}
              </div>
            </div>
          </div>
          <button onClick={openPortal} disabled={portalLoading} className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white text-sm font-bold rounded-sm hover:bg-zinc-800 disabled:opacity-60 flex-shrink-0" data-testid="open-portal-btn">
            {portalLoading ? <><CircleNotch className="animate-spin" size={14} /> Opening…</> : <><CreditCard size={14} weight="bold" /> Manage subscription <ArrowSquareOut size={12} weight="bold" /></>}
          </button>
        </div>
      ) : sub?.status === 'canceled' ? (
        <div className="border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">Your subscription was canceled. Pick a plan below to reactivate.</div>
        </div>
      ) : null}

      {/* Billing toggle + quantity */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="flex items-center gap-2 bg-zinc-100 p-1 rounded-sm w-fit">
          <button onClick={() => setAnnual(false)} className={`px-4 py-1.5 text-xs font-bold rounded-sm transition-colors ${!annual ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-600'}`} data-testid="billing-monthly-toggle">Monthly</button>
          <button onClick={() => setAnnual(true)} className={`px-4 py-1.5 text-xs font-bold rounded-sm transition-colors ${annual ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-600'}`} data-testid="billing-annual-toggle">Annual <span className="ml-1 text-[10px] text-emerald-600">−20%</span></button>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">How many properties?</label>
            <input type="number" min="1" max="100" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-28 px-3 py-2 border border-zinc-300 text-sm rounded-sm focus:outline-none focus:border-[#0052FF]" data-testid="billing-quantity" />
          </div>
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid md:grid-cols-3 gap-0 border border-zinc-200 bg-white" data-testid="plans-grid">
        {plans.map((p, i) => {
          const price = annual ? p.annual_price : p.monthly_price;
          const total = price * qty;
          const isCurrent = hasActiveSub && sub.plan_key === p.key && (sub.interval === (annual ? 'year' : 'month'));
          const popular = p.key === 'pro';
          return (
            <div key={p.key} className={`p-6 relative ${i < plans.length - 1 ? 'border-r border-zinc-200' : ''} ${popular ? 'bg-zinc-950 text-white' : ''}`} data-testid={`plan-card-${p.key}`}>
              {popular && <div className="absolute top-0 right-0 bg-[#0052FF] text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-wider">Most popular</div>}
              <div className={`text-[11px] font-bold uppercase tracking-[0.15em] ${popular ? 'text-[#0052FF]' : 'text-zinc-500'} mb-3`}>{p.name}</div>
              <div className={`flex items-baseline gap-1 mb-1 ${popular ? 'text-white' : 'text-zinc-950'}`}>
                <span className="text-4xl font-black" style={{ fontFamily: 'var(--font-heading)' }}>${price}</span>
                <span className="text-sm font-medium">/property/mo</span>
              </div>
              <div className={`text-xs ${popular ? 'text-zinc-400' : 'text-zinc-500'} mb-5`}>
                {annual ? `Billed annually ($${total * 12}/yr for ${qty} ${qty === 1 ? 'property' : 'properties'})` : `Billed monthly ($${total}/mo for ${qty} ${qty === 1 ? 'property' : 'properties'})`}
              </div>
              <button
                onClick={() => startCheckout(p.key)}
                disabled={!!startingCheckout || isCurrent}
                className={`w-full py-2.5 text-sm font-bold rounded-sm transition-colors mb-5 inline-flex items-center justify-center gap-1.5 ${isCurrent ? 'bg-zinc-200 text-zinc-500 cursor-default' : popular ? 'bg-[#0052FF] text-white hover:bg-[#003EDD]' : 'bg-zinc-950 text-white hover:bg-zinc-800'} disabled:opacity-60`}
                data-testid={`plan-cta-${p.key}`}
              >
                {startingCheckout === p.key ? <><CircleNotch className="animate-spin" size={14} /> Redirecting…</> : isCurrent ? <>Current plan</> : hasActiveSub ? <>Switch to {p.name}</> : <>Start {p.name}</>}
              </button>
              <ul className="space-y-2">
                {PLAN_FEATURES[p.key]?.map((f, j) => (
                  <li key={j} className={`flex items-start gap-2 text-xs ${popular ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    <Check size={12} weight="bold" className={popular ? 'text-[#0052FF] mt-0.5 flex-shrink-0' : 'text-[#0052FF] mt-0.5 flex-shrink-0'} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-zinc-500 space-y-1">
        <p>⚡ Secure payments by Stripe · All major cards accepted · Cancel anytime via the billing portal.</p>
        <p>Prices in USD. Annual plans billed upfront at a 20% discount.</p>
      </div>
    </div>
  );
}
